'use client';

import {
  ElementType,
  HTMLProps,
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { createPortal } from 'react-dom';

import { Icons } from 'app/constants/icons';
import { cn } from 'app/utils/styles.utils';
import { isInput } from 'app/utils/html.utils';

declare module 'react' {
  // eslint-disable-next-line
  interface HTMLAttributes<T> {
    'data-modal-toggle'?: string;
    'data-modal-data'?: unknown;
  }
}

type AnimationType = 'fade' | 'slide';

type ModalContextProps<T = unknown> = {
  setOpen: (isOpen: boolean) => void;
  data: T;
};

const ModalContext = createContext<ModalContextProps>(null!);

const ARIA_CLOSE_LABEL = 'Close';

type ModalProps = HTMLProps<HTMLDialogElement> & {
  className?: string;
  modalClassName?: string;
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  animation?: AnimationType | null;
  animationIn?: AnimationType | null;
  animationOut?: AnimationType | null;
  cancellable?: boolean;
  hideCloseButton?: boolean;
  CloseButton?: ElementType<{ onClick: () => void; 'aria-label': string }>;
  isCenteredModal?: boolean;
};

export const Modal = memo(
  ({
    id,
    data: dataProp,
    isOpen: isOpenProp,
    setIsOpen: setIsOpenProp,
    ...props
  }: ModalProps) => {
    const [isOpenLocal, setIsOpenLocal] = useState(false);

    const setIsOpen = setIsOpenProp ?? setIsOpenLocal;
    const isOpen = isOpenProp ?? isOpenLocal;

    const [data, setData] = useState<unknown>(dataProp);

    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;

    // handle data props
    useEffect(() => {
      if (!id) return;

      const handleClick = (event: MouseEvent) => {
        const el = event.target as HTMLElement;
        const targetContainer = el?.closest(
          `[data-modal-toggle="${id}"]`,
        ) as HTMLElement;

        if (targetContainer?.dataset.modalToggle !== id) return;

        setData(targetContainer.dataset.modalData ?? null);
        setIsOpen(!isOpenRef.current);
      };

      document.body.addEventListener('click', handleClick);

      return () => {
        document.body.removeEventListener('click', handleClick);
      };
    }, [id, setIsOpen]);

    if (!isOpen) return null;

    return createPortal(
      <ModalRender id={id} setIsOpen={setIsOpen} data={data} {...props} />,
      document.body,
    );
  },
);

Modal.displayName = 'Modal';

type ModalRenderProps = Omit<ModalProps, 'setIsOpen' | 'isOpen' | 'data'> & {
  setIsOpen: (isOpen: boolean) => void;
  data: unknown;
};

const ModalRender = ({
  setIsOpen: setIsOpenProp,
  animation = 'slide',
  animationIn = animation,
  animationOut = animation,
  cancellable = true,
  className,
  modalClassName,
  hideCloseButton,
  CloseButton,
  children,
  data,
  isCenteredModal,
  ...props
}: ModalRenderProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isOpeningRef = useRef(!animationIn);

  const setIsOpen = useCallback(
    (isOpen: boolean) => {
      const dialog = dialogRef.current;
      const container = containerRef.current;

      if (!isOpeningRef.current || !dialog || !container) return;

      if (isOpen) {
        delete dialog.dataset.closing;
        delete container.dataset.closing;

        return;
      }

      dialog.dataset.closing = 'true';
      container.dataset.closing = 'true';
      dialog.close();

      if (!animationOut) {
        setIsOpenProp(isOpen);

        return;
      }

      const handleAnimationEnd = () => {
        setIsOpenProp(isOpen);

        container.removeEventListener('animationend', handleAnimationEnd);
      };

      container.addEventListener('animationend', handleAnimationEnd);
    },
    [setIsOpenProp, animationOut],
  );

  const handleCloseButtonClick = useCallback(
    () => setIsOpen(false),
    [setIsOpen],
  );

  useEffect(() => {
    const dialog = dialogRef.current;
    const container = containerRef.current;
    const prevOverflow = document.body.style.overflow;

    if (prevOverflow !== 'hidden') {
      document.body.dataset.prevOverflow = prevOverflow;
    }

    document.body.style.overflow = 'hidden';

    // check if there is no any input focused and focus the dialog
    if (document.activeElement && !isInput(document.activeElement)) {
      dialog?.focus();
    }

    let isPointerDown = false;

    const handleClick = (event: MouseEvent) => {
      if (!cancellable) return;

      if (event.target !== containerRef.current) {
        isPointerDown = false;

        return;
      }

      if (event.type === 'pointerdown') {
        isPointerDown = true;

        return;
      }

      if (!isPointerDown) {
        return;
      }

      setIsOpen(false);
    };

    // handle ESC key
    const handleKeydown = (event: KeyboardEvent) => {
      if (!cancellable || event.key !== 'Escape') return;

      // check if there is any input focused
      if (document.activeElement && isInput(document.activeElement)) return;

      // check if this dialog is the top one
      const dialogs = document.querySelectorAll('[data-modal-base]');

      if (dialogs[dialogs.length - 1] !== dialog) return;

      event.preventDefault();

      setIsOpen(false);
    };

    const handleAnimationEnd = () => {
      isOpeningRef.current = true;
    };

    dialog?.addEventListener('pointerdown', handleClick);
    dialog?.addEventListener('pointerup', handleClick);
    container?.addEventListener('animationend', handleAnimationEnd);
    document.addEventListener('keydown', handleKeydown);

    return () => {
      dialog?.removeEventListener('pointerdown', handleClick);
      dialog?.removeEventListener('pointerup', handleClick);
      container?.removeEventListener('animationend', handleAnimationEnd);
      document.removeEventListener('keydown', handleKeydown);

      if (!document.querySelector('[data-modal-base]')) {
        document.body.style.overflow =
          document.body.dataset.prevOverflow ?? 'scroll';
      }
    };
  }, [cancellable, setIsOpen]);

  return (
    <ModalContext.Provider value={{setOpen: setIsOpen, data}}>
      <dialog
        ref={dialogRef}
        data-modal-base
        className={cn(
          'fixed left-0 top-0 z-[200] flex h-full w-full transition-colors',
          'bg-[rgba(0,0,0,0.6)] light:bg-[rgba(197,197,197,0.2)]',
          'open:animate-modal-in',
          'data-[closing]:animate-modal-out',
          'duration-1000',
          'transition-all',
          modalClassName,
        )}
        open
        {...props}>
        <div
          role="group"
          ref={containerRef}
          className={cn(
            'flex-wrap',
            'absolute left-0 top-0 flex h-full w-full items-center justify-center',
            !isCenteredModal && 'mobile:bottom-0 mobile:top-auto',
            !isCenteredModal && 'mobile:items-end',
            getAnimationClasses(animationIn, animationOut),
          )}>
          <div
            className={cn(
              'relative rounded-xl bg-[--modal-background-color] p-4',
              className,
              !isCenteredModal &&
                'mobile:w-full mobile:rounded-b-none mobile:rounded-t-[20px]',
            )}>
            {children}

            {!hideCloseButton &&
              cancellable &&
              (CloseButton ? (
                <CloseButton
                  aria-label={ARIA_CLOSE_LABEL}
                  onClick={handleCloseButtonClick}
                />
              ) : (
                <button
                  aria-label={ARIA_CLOSE_LABEL}
                  onClick={handleCloseButtonClick}
                  className={cn(
                    'absolute right-[1rem] top-2.5 flex h-[21px] w-[21px] items-center justify-center rounded-full bg-gray-50 transition-colors hover:bg-gray-100 active:bg-neutral-200',
                    !isCenteredModal && 'mobile:hidden',
                  )}>
                  <Icons.x className="text-black" size="19" />
                </button>
              ))}
          </div>
        </div>
      </dialog>
    </ModalContext.Provider>
  );
};

const getAnimationClasses = (
  animationIn: AnimationType | null,
  animationOut: AnimationType | null,
) => {
  // use static classes (they can't be dynamic since tailwind doesn't support it)

  return cn({
    // fade
    'animate-fade-in': animationIn === 'fade',
    'data-[closing]:animate-fade-out': animationOut === 'fade',

    // slide
    'animate-slide-in mobile:animate-slide-in-mobile': animationIn === 'slide',
    'data-[closing]:animate-slide-out mobile:data-[closing]:animate-slide-out-mobile':
      animationOut === 'slide',
  });
};

export const useModal = <T = unknown,>() => {
  return (useContext(ModalContext) ?? {}) as ModalContextProps<T>;
};
